
import on_domready from "domready"
import log from "plugins/loglevel"
import Promise from "plugins/bluebird"
import Auth_manager from "app/authentication/manager"

import {
  requestIdleCallback,
} from "plugins/request-animation-frame-polyfill"

import WaitObjectByPathPromise from "app/wait_object_by_path_promise"
import {
  isSignedIn,
} from "app/authentication/state"

import {
  trackSignUp,
} from "app/authentication/tracking"

import {
  refreshAndUpdateComponentPromise,
} from "app/authentication/remote_state"

import {
  captureMessage,
} from "app/error_reporting"

import Notifier from "app/notifier/index"


import I18n from "plugins/i18n-js"

// https://developers.google.com/identity/one-tap/web/reference/js-reference#CredentialResponse
interface GoogleYoloCredentialResponse {
  credential: string
  select_by: string
  client_id: string
}
// https://developers.google.com/identity/sign-in/web/backend-auth#verify-the-integrity-of-the-id-token
interface GoogleYoloCredentialDecodedData {
  // OUR app's client ID
  aud: string
  // Should be `accounts.google.com` or `https://accounts.google.com`
  iss: string
  // Expiry time, should be unix time
  exp: number

  //region User data
  email: string
  email_verified: boolean
  name: string
  // URL
  picture: string
  //endregion

  [key: string]: any
}
// https://developers.google.com/identity/one-tap/web/reference/js-reference#PromptMomentNotification
interface PromptMomentNotification {
  isDisplayMoment: () => boolean
  isDisplayed: () => boolean

  isNotDisplayed: () => boolean
  getNotDisplayedReason: () => string

  isSkippedMoment: () => boolean
  getSkippedReason: () => string

  isDismissedMoment: () => boolean
  getDismissedReason: () => string

  getMomentType: () => string
}

interface GoogleYoloHintInputs {
  supportedAuthMethods: string[],
  supportedIdTokenProviders: Array<{
    uri:      string,
    clientId: string,
  }>,
}

interface GoogleYolo {
  hint: (object: GoogleYoloHintInputs) => Promise<any>,
}

type OnGoogleYoloLoad = (yolo: GoogleYolo) => void

const GOOGLE_CLIENT_ID = "872042080587.apps.googleusercontent.com"

declare global {
  interface Window {
    onGoogleYoloLoad: OnGoogleYoloLoad | null,
    // https://developers.google.com/identity/one-tap/web/guides/migration/hint-only-integration?hl=en
    onGoogleLibraryLoad: () => void | null,

    google: null | {
      accounts: null | {
        id: null | {
          initialize: (opts: {
            client_id:  string,
            callback:   (response: GoogleYoloCredentialResponse) => void,
            use_fedcm_for_prompt: boolean;
          }) => void
          prompt: (callback?: (notification: PromptMomentNotification) => void) => void
        }
      }
    },
  }
}

const RejectionReasons = {
  ERROR_REPORTED: "ERROR_REPORTED",
}

const onLoad = () => {
  on_domready(() => {
    if (isSignedIn()) {
      return
    }

    requestIdleCallback(() => {
      WaitObjectByPathPromise("google.accounts.id", {
        should_log_timeout: true,
      })
      .then(() => {
        import(
          "app/resources/users/create_with_google/index"
        )
        .then(({
          create: create_user_with_google,
          ResultTypeEnums: CreateUserResultTypes,
        }) => {
          if (window.google == null ||
            window.google.accounts == null ||
            window.google.accounts.id == null) {
            return
          }

          window.google.accounts.id.initialize({
            client_id:            "872042080587.apps.googleusercontent.com",
            // Allow the browser to control user sign-in prompts and mediate the sign-in flow between your website and Google.
            // https://developers.google.com/identity/gsi/web/reference/js-reference#use_fedcm_for_prompt
            use_fedcm_for_prompt: true,

            callback:   (response) => {
              if (isSignedIn()) { return }

              return Promise.resolve(
                import("jsontokens")
              )
              .then(({decodeToken}) => {
                const data = decodeToken(response.credential).payload as GoogleYoloCredentialDecodedData
                if (data.aud !== GOOGLE_CLIENT_ID) { return }
                if (!["https://accounts.google.com", "accounts.google.com"].includes(data.iss)) { return }

                const current_unix_time = Math.round((new Date()).getTime() / 1000)
                // If current time passed token expiry time
                if (data.exp < current_unix_time) { return }

                create_user_with_google({
                  email:      data.email,
                  full_name:  data.name,
                  image:      data.picture,
                  // It seems we are using the JWT as access token
                  // Keeping passing it unless it affects security or function
                  id_token:   response.credential,
                })
                .then(({result_type}) => {
                  switch (result_type) {
                    case CreateUserResultTypes.SUCCESS:
                      Auth_manager.emit("sign_up:success")
                      return
                    case CreateUserResultTypes.CONFLICT:
                      return
                    case CreateUserResultTypes.UNAUTHORIZED:
                      return
                    default:
                      captureMessage(
                        `unexpected/unhandled result_type: <${result_type}>`,
                        {
                          source_file_path:
                            "app/googleyolo/onLoad",
                          response_credential:  response.credential,
                          data:                 data,
                        },
                      )
                      throw RejectionReasons.ERROR_REPORTED
                  }
                })
              })
            },
          })
          window.google.accounts.id.prompt((notification) => {
            log.debug("window.google.accounts.id.prompt callback response")
            log.debug({
              isDisplayMoment: notification.isDisplayMoment(),
              isDisplayed: notification.isDisplayed(),

              isNotDisplayed: notification.isNotDisplayed(),
              getNotDisplayedReason: notification.getNotDisplayedReason(),

              isSkippedMoment: notification.isSkippedMoment(),
              getSkippedReason: notification.getSkippedReason(),

              isDismissedMoment: notification.isDismissedMoment(),
              getDismissedReason: notification.getDismissedReason(),

              getMomentType: notification.getMomentType(),
            })
          })
        })
      })
    })
  })
}

export default {onLoad}
